import { formatDateTimeBR } from '../../utils/dateUtils';
import { ClockIcon, CalendarIcon, UserIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { colaboradoresService } from '../../services/colaboradores.service';
import { Loader2 } from 'lucide-react';

interface HistoricoTabProps {
  colaboradorId: number;
}

const tipoConfig = {
  exame: {
    label: 'Exame',
    color: 'bg-green-100 text-green-800',
    icon: CalendarIcon,
  },
  cadastro: {
    label: 'Cadastro',
    color: 'bg-green-100 text-green-800',
    icon: UserIcon,
  },
  alteracao: {
    label: 'Alteração',
    color: 'bg-yellow-100 text-yellow-800',
    icon: ClockIcon,
  },
};

export default function HistoricoTab({ colaboradorId }: HistoricoTabProps) {
  const { data: historico = [], isLoading } = useQuery({
    queryKey: ['colaborador-historico', colaboradorId],
    queryFn: () => colaboradoresService.getHistorico(colaboradorId),
  });

  const formatDateTime = (dateString: string) => {
    try {
      return formatDateTimeBR(dateString);
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--h1Color)' }}>
          Histórico de Atividades
        </h3>
        <p className="text-sm text-gray-600">
          Registro completo de todas as atividades relacionadas a este colaborador
        </p>
      </div>

      {/* Timeline */}
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        <div className="space-y-6">
          {historico.map((item) => {
            const tipo = tipoConfig[item.tipo as keyof typeof tipoConfig] || tipoConfig.alteracao;
            const TipoIcon = tipo.icon;

            return (
              <div key={item.id} className="relative flex gap-4">
                <div className="relative z-10 flex-shrink-0">
                  <div className={`p-2 rounded-full ${tipo.color}`}>
                    <TipoIcon className="w-4 h-4" />
                  </div>
                </div>
                <div className="flex-1 card">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.acao}</h4>
                      <p className="text-sm text-gray-600 mt-1">{item.detalhes}</p>
                    </div>
                    <div className="text-sm text-gray-500 whitespace-nowrap">
                      {formatDateTime(item.data)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <UserIcon className="w-3 h-3" />
                    <span>{item.usuario}</span>
                    <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded">
                      {tipo.label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {historico.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          Nenhum histórico encontrado
        </div>
      )}
    </div>
  );
}
